import { useAtomValue } from 'jotai';

import { Group, ListViewGroup, ListViewRow, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { DetailedInfoLineItem } from '@common/components/DetailedInfoLineItem';
import { EmptySection } from '@common/components/EmptySection';
import { formatCurrency } from '@common/lib/currency';

import { useGetOrderPaymentFai } from '../../api/useGetOrderPaymentFai';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';
import { SectionSkeleton } from '../order-details/shared/SectionSkeleton';

const SECTION_TITLE = 'F&I aftermarket';

interface FaiAftermarketItem {
	name: string;
	sellingPrice: number;
	provider?: string;
	dealerCost?: number;
	netProfit?: number;
}

/**
 * Shared component that renders F&I aftermarket items.
 * Used in both the order details drawer and the F&I cell popover.
 */
export const FaiAftermarketDetails = ({
	faiAftermarket,
}: {
	faiAftermarket: FaiAftermarketItem[] | undefined;
}) => {
	return (
		<Stack itemsSpacing='16'>
			{faiAftermarket && faiAftermarket.length > 0 ? (
				<ListViewGroup>
					{faiAftermarket.map((item, index) => (
						<ListViewRow
							key={index}
							bottomContent={
								<DetailedInfoLineItem
									primaryLabel={item.name}
									secondaryLabel={
										item.provider ? `Provider: ${item.provider}` : undefined
									}
									rightAccessory={
										<Stack itemsSpacing='4'>
											<Group
												itemsAlignX='space-between'
												itemsSpacing='8'
												noWrap>
												<Text size='footnote' color='secondary'>
													Selling price
												</Text>
												<Text size='bodySmall'>
													{formatCurrency(item.sellingPrice)}
												</Text>
											</Group>
											{item.dealerCost !== undefined && (
												<Group
													itemsAlignX='space-between'
													itemsSpacing='8'
													noWrap>
													<Text size='footnote' color='secondary'>
														Dealer cost
													</Text>
													<Text size='bodySmall'>
														{formatCurrency(item.dealerCost)}
													</Text>
												</Group>
											)}
											{item.netProfit !== undefined && (
												<Group
													itemsAlignX='space-between'
													itemsSpacing='8'
													noWrap>
													<Text size='footnote' color='secondary'>
														Net profit
													</Text>
													<Text
														size='bodySmall'
														weight='semibold'
														color='success'>
														{formatCurrency(item.netProfit)}
													</Text>
												</Group>
											)}
										</Stack>
									}
								/>
							}
						/>
					))}
				</ListViewGroup>
			) : (
				<EmptySection message='No F&I aftermarket items on this order' />
			)}
		</Stack>
	);
};

export const FaiAftermarketSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderPaymentFai, isLoading } = useGetOrderPaymentFai(
		selectedRow?.purchasedVehicleId || undefined,
	);

	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!orderPaymentFai) {
		return null;
	}

	const { faiAftermarket } = orderPaymentFai;

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<FaiAftermarketDetails faiAftermarket={faiAftermarket} />
		</AccordionSection>
	);
};
